import { memo, useMemo } from "react";

import { useProduct } from "@shopify/hydrogen-react";

import { Carousel } from "@components/Carousel";
import { InnerArrows } from "@components/Carousel/extensions";
import { ProductCardOption } from "@components/ProductCard/ProductCardOption.data";
import { isColorOption } from "@react/utilities";
import { formatProduct } from "@tablescape/utilities";
import { tw } from "classnames";

const styles = {
  base: tw("flex", "flex-wrap", "gap-2"),
  carousel: tw("items-center", "overflow-x-auto"),
};

type props = {
  nextLabel: string;
  prevLabel: string;
};

const ProductCardOptionsBase = ({ nextLabel, prevLabel }: props) => {
  const { product } = useProduct();

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const colorOption = useMemo(
    () => formattedProduct.options?.find((option) => isColorOption(option)),
    [formattedProduct.options]
  );

  if (!colorOption) {
    return null;
  }

  return (
    <div className={styles.base}>
      <Carousel
        className={styles.carousel}
        extensions={{ InnerArrows: InnerArrows(nextLabel, prevLabel) }}
        options={{
          perPage: 4,
          gap: 8,
          pagination: false,
          arrows: false,
          drag: false,
          mediaQuery: "min",
          breakpoints: {
            768: {
              perPage: 5,
            },
            1280: {
              perPage: 6,
            },
            1440: { perPage: 8 },
          },
        }}
      >
        {colorOption.optionValues.map((value) => (
          <ProductCardOption
            key={value.name}
            optionName={colorOption.name}
            optionValue={value}
          />
        ))}
      </Carousel>
    </div>
  );
};

export const ProductCardOptions = memo(ProductCardOptionsBase);
